'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { locauxService, usersService, documentsService } from '@/lib/supabase/services';
import { analyzeLocalText, ExtractedLocalData } from '@/lib/ai/analyzeLocalText';
import { useAuth } from '@/contexts/AuthContext';

interface NouveauLocalContentProps {
  mode: 'create' | 'edit';
  editId?: string;
}

const VILLES = ['Casablanca', 'Rabat', 'Marrakech', 'Fès', 'Tanger', 'Agadir', 'Meknès', 'Oujda'];
const TYPES = ['Rue principale', 'Angle de rue', 'Centre commercial', 'Pied d\'immeuble', 'Galerie marchande', 'Retail park', 'Autre'];
const STATUTS = ['Brouillon', 'En revue', 'Publié', 'À fort potentiel', 'Visité', 'En discussion', 'Offre reçue', 'En négociation', 'Loué / retiré'];
const SOURCES = ['Prospection directe', 'Réseau', 'Propriétaire entrant', 'Partenaire', 'Recommandation', 'Autre'];
const TYPES_DETENTION = ['Pleine propriété', 'Copropriété', 'Bail commercial', 'Emphytéose', 'Autre'];
const USAGES = ['Commerce de détail', 'Restauration', 'Services', 'Banque / Assurance', 'Santé / Beauté', 'Loisirs', 'Mixte'];
const ETATS = ['Neuf', 'Bon état', 'À rénover', 'Gros travaux'];
const TYPES_LOCAL_COMMERCIAL = ['Local commercial', 'Terrain nu', 'Autres'];
const OFFRES = ['A louer', 'A vendre'];

interface FormData {
  // Identification
  titre: string;
  statut: string;
  source: string;
  broker: string;
  brokerId: string;
  ownerId: string;
  createdBy: string;
  priorite: string;
  typeLocalCommercial: string;
  offre: string;
  // Localisation
  ville: string;
  quartier: string;
  adresse: string;
  zoneCommerciale: string;
  lat: string;
  lng: string;
  // Surfaces
  surfaceTotal: string;
  surfaceRDC: string;
  surfaceMezzanine: string;
  surfaceSousSol: string;
  surfaceEtage: string;
  surfaceTerrasse: string;
  // Façade
  facade: string;
  lineaireVitrine: string;
  nbVitrines: string;
  // Juridique
  proprietaire: string;
  typeDetention: string;
  titreFoncier: string;
  usageAutorise: string;
  noteRenseignement: string;
  // Commercial
  loyer: string;
  droitEntree: string;
  pasDePorte: string;
  depotGarantie: string;
  disponibilite: string;
  exclusivite: boolean;
  // Technique
  hauteurPlafond: string;
  puissanceElectrique: string;
  extraction: boolean;
  climatisation: boolean;
  accessibilite: boolean;
  parking: string;
  livraison: boolean;
  etatLocal: string;
  travauxNecessaires: string;
  // Environnement
  fluxPieton: string;
  visibiliteEnseigne: string;
  voisinage: string;
  proximiteTransports: string;
  // Notes
  commentairesPublics: string;
  commentairesConfidentiels: string;
  // AI enrichment (not a form field, used for matching)
  aiExtractedData?: ExtractedLocalData | null;
  // typeLocal (used in localisation section)
  typeLocal?: string;
}

const defaultForm: FormData = {
  titre: '', statut: 'Brouillon', source: '', broker: '', brokerId: '', ownerId: '', createdBy: '', priorite: 'Normale',
  typeLocalCommercial: 'Local commercial', offre: 'A louer',
  ville: '', quartier: '', adresse: '', zoneCommerciale: '', lat: '', lng: '',
  surfaceTotal: '', surfaceRDC: '', surfaceMezzanine: '', surfaceSousSol: '', surfaceEtage: '', surfaceTerrasse: '',
  facade: '', lineaireVitrine: '', nbVitrines: '',
  proprietaire: '', typeDetention: '', titreFoncier: '', usageAutorise: '', noteRenseignement: '',
  loyer: '', droitEntree: '', pasDePorte: '', depotGarantie: '', disponibilite: '', exclusivite: false,
  hauteurPlafond: '', puissanceElectrique: '', extraction: false, climatisation: false, accessibilite: false,
  parking: '', livraison: false, etatLocal: '', travauxNecessaires: '',
  fluxPieton: '', visibiliteEnseigne: '', voisinage: '', proximiteTransports: '',
  commentairesPublics: '', commentairesConfidentiels: '',
  aiExtractedData: null,
  typeLocal: '',
};

const SECTIONS = [
  { id: 'identification', label: 'Identification', icon: '🏷️' },
  { id: 'localisation', label: 'Localisation', icon: '📍' },
  { id: 'surfaces', label: 'Surfaces', icon: '📐' },
  { id: 'facade', label: 'Façade & Vitrine', icon: '🪟' },
  { id: 'juridique', label: 'Juridique', icon: '⚖️' },
  { id: 'commercial', label: 'Commercial', icon: '💰' },
  { id: 'technique', label: 'Technique', icon: '🔧' },
  { id: 'environnement', label: 'Environnement', icon: '🌍' },
  { id: 'notes', label: 'Notes & Commentaires', icon: '📝' },
];

function computeCompletude(form: FormData): number {
  const fields = [
    form.titre, form.ville, form.quartier, form.adresse,
    form.surfaceTotal, form.surfaceRDC, form.loyer, form.disponibilite,
    form.proprietaire, form.facade, form.hauteurPlafond,
    form.source, form.broker, form.zoneCommerciale, form.lat, form.lng,
    form.surfaceMezzanine, form.surfaceSousSol, form.lineaireVitrine,
    form.typeDetention, form.titreFoncier, form.usageAutorise,
    form.droitEntree, form.pasDePorte, form.depotGarantie,
    form.puissanceElectrique, form.parking, form.etatLocal,
    form.fluxPieton, form.voisinage, form.commentairesPublics,
  ];
  const filled = fields.filter(v => v && String(v).trim() !== '').length;
  return Math.round((filled / fields.length) * 100);
}

export default function NouveauLocalContent({ mode, editId }: NouveauLocalContentProps) {
  const router = useRouter();
  const { user, userProfile } = useAuth();
  const [form, setForm] = useState<FormData>(defaultForm);
  const [activeSection, setActiveSection] = useState('identification');
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
  const [uploadedDocs, setUploadedDocs] = useState<File[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  const [deletingPhoto, setDeletingPhoto] = useState<string | null>(null);
  const [brokers, setBrokers] = useState<{ id: string; label: string; email: string }[]>([]);
  const [owners, setOwners] = useState<{ id: string; label: string; email: string }[]>([]);
  const [analyzingText, setAnalyzingText] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedLocalData | null>(null);
  const [showExtractedPanel, setShowExtractedPanel] = useState(false);
  const photoInputRef = React.useRef<HTMLInputElement>(null);
  const docInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    usersService.getAll().then(users => {
      const brokerUsers = users.filter(u =>
        u.role === 'Broker' || u.role === 'Broker Senior' || u.role === 'Administrateur'
      );
      const brokerList = brokerUsers.map(u => ({
        id: u.id,
        label: `${u.prenom} ${u.nom}`.trim() || u.email,
        email: u.email,
      }));
      setBrokers(brokerList);

      const ownerUsers = users.filter(u => u.role === 'Propriétaire');
      setOwners(ownerUsers.map(u => ({
        id: u.id,
        label: `${u.prenom} ${u.nom}`.trim() || u.email,
        email: u.email,
      })));

      // In create mode, auto-assign the current user as broker and createdBy
      if (mode === 'create' && user?.id) {
        const currentBroker = brokerList.find(b => b.id === user.id);
        setForm(prev => ({
          ...prev,
          createdBy: user.id,
          brokerId: currentBroker ? user.id : (prev.brokerId || user.id),
          broker: currentBroker ? currentBroker.label : (prev.broker || (userProfile ? `${userProfile.prenom} ${userProfile.nom}`.trim() || userProfile.email : '')),
        }));
      }
    });
  }, [mode, user?.id, userProfile]);

  // Ensure createdBy is always set as soon as user is available (fallback)
  useEffect(() => {
    if (mode === 'create' && user?.id) {
      setForm(prev => ({
        ...prev,
        createdBy: prev.createdBy || user.id,
        brokerId: prev.brokerId || user.id,
      }));
    }
  }, [mode, user?.id]);

  // Load existing data in edit mode
  useEffect(() => {
    if (mode === 'edit' && editId) {
      setLoadingEdit(true);
      Promise.all([
        locauxService.getFullById(editId),
        locauxService.getPhotoUrls(editId),
      ]).then(([raw, photoUrls]) => {
        if (raw) {
          setForm({
            titre: raw.title || '',
            statut: raw.status || 'Brouillon',
            source: raw.source || '',
            broker: raw.broker || '',
            brokerId: raw.broker_id || '',
            ownerId: raw.owner_id || '',
            createdBy: raw.created_by || '',
            priorite: raw.priorite || 'Normale',
            typeLocalCommercial: raw.type_local || 'Local commercial',
            offre: raw.offre || 'A louer',
            ville: raw.city || '',
            quartier: raw.quartier || '',
            adresse: raw.address || '',
            zoneCommerciale: raw.zone_commerciale || '',
            typeLocal: raw.type || '',
            lat: raw.lat ? String(raw.lat) : '',
            lng: raw.lng ? String(raw.lng) : '',
            surfaceTotal: raw.surface_total ? String(raw.surface_total) : '',
            surfaceRDC: raw.surface_rdc ? String(raw.surface_rdc) : '',
            surfaceMezzanine: raw.surface_mezzanine ? String(raw.surface_mezzanine) : '',
            surfaceSousSol: raw.surface_sous_sol ? String(raw.surface_sous_sol) : '',
            surfaceEtage: raw.surface_etage ? String(raw.surface_etage) : '',
            surfaceTerrasse: raw.surface_terrasse ? String(raw.surface_terrasse) : '',
            facade: raw.facade ? String(raw.facade) : '',
            lineaireVitrine: raw.lineaire_vitrine ? String(raw.lineaire_vitrine) : '',
            nbVitrines: raw.nb_vitrines ? String(raw.nb_vitrines) : '',
            proprietaire: raw.proprietaire || '',
            typeDetention: raw.type_detention || '',
            titreFoncier: raw.titre_foncier || '',
            usageAutorise: raw.usage_autorise || '',
            noteRenseignement: raw.note_renseignement || '',
            loyer: raw.loyer ? String(raw.loyer) : '',
            droitEntree: raw.droit_entree ? String(raw.droit_entree) : '',
            pasDePorte: raw.pas_de_porte ? String(raw.pas_de_porte) : '',
            depotGarantie: raw.depot_garantie ? String(raw.depot_garantie) : '',
            disponibilite: raw.disponibilite || '',
            exclusivite: Boolean(raw.exclusivite),
            hauteurPlafond: raw.hauteur_plafond ? String(raw.hauteur_plafond) : '',
            puissanceElectrique: raw.puissance_electrique || '',
            extraction: Boolean(raw.extraction),
            climatisation: Boolean(raw.climatisation),
            accessibilite: Boolean(raw.accessibilite),
            parking: raw.parking || '',
            livraison: Boolean(raw.livraison),
            etatLocal: raw.etat_local || '',
            travauxNecessaires: raw.travaux_necessaires || '',
            fluxPieton: raw.flux_pieton || '',
            visibiliteEnseigne: raw.visibilite_enseigne || '',
            voisinage: raw.voisinage || '',
            proximiteTransports: raw.proximite_transports || '',
            commentairesPublics: raw.commentaires_publics || '',
            commentairesConfidentiels: raw.commentaires_confidentiels || '',
          });
        }
        setExistingPhotos(photoUrls);
        setLoadingEdit(false);
      });
    }
  }, [mode, editId]);

  const completude = computeCompletude(form);

  const set = (field: keyof FormData, value: string | boolean) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleDeleteExistingPhoto = async (photoUrl: string) => {
    if (!editId) return;
    setDeletingPhoto(photoUrl);
    try {
      // Extract the storage path from the URL
      // URL format: .../storage/v1/object/public/local-photos/{localId}/{filename}
      const urlParts = photoUrl.split('/local-photos/');
      if (urlParts.length < 2) {
        toast.error('Impossible de supprimer cette photo');
        setDeletingPhoto(null);
        return;
      }
      const storagePath = urlParts[1];
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { error } = await supabase.storage.from('local-photos').remove([storagePath]);
      if (error) {
        toast.error('Erreur lors de la suppression');
      } else {
        setExistingPhotos(prev => prev.filter(p => p !== photoUrl));
        // Update has_photos flag if no photos left
        const remaining = existingPhotos.filter(p => p !== photoUrl);
        if (remaining.length === 0 && uploadedPhotos.length === 0) {
          await locauxService.update(editId, { hasPhotos: false });
        }
        toast.success('Photo supprimée');
      }
    } catch {
      toast.error('Erreur lors de la suppression');
    }
    setDeletingPhoto(null);
  };

  const handleSave = async (publish = false) => {
    setSaving(true);

    if (mode === 'edit' && editId) {
      // Update existing local
      const result = await locauxService.updateFull(editId, form, computeCompletude(form), publish);
      setSaving(false);
      if (!result) {
        toast.error('Erreur lors de la mise à jour. Veuillez réessayer.');
        return;
      }
      // Upload new photos if any
      if (uploadedPhotos.length > 0) {
        const uploadedUrls = await locauxService.uploadPhotos(result.id, uploadedPhotos);
        if (uploadedUrls.length > 0) {
          await locauxService.update(result.id, { hasPhotos: true });
        }
      }
      // Upload new documents if any
      if (uploadedDocs.length > 0) {
        const raw = await locauxService.getFullById(editId);
        for (const docFile of uploadedDocs) {
          await documentsService.create(
            { type: 'Technique', local: result.title, localRef: raw?.ref || '' },
            docFile
          );
        }
        await locauxService.update(result.id, { hasDocs: true });
      }
      toast.success('Modifications sauvegardées !');
      setTimeout(() => router.push(`/locaux/${result.id}`), 1000);
    } else {
      // Create new local
      const result = await locauxService.createFull(form, computeCompletude(form), publish);
      setSaving(false);
      if (!result) {
        toast.error('Erreur lors de l\'enregistrement. Vérifiez votre connexion et réessayez.');
        return;
      }
      // Upload photos to Supabase Storage if any
      if (uploadedPhotos.length > 0) {
        const uploadedUrls = await locauxService.uploadPhotos(result.id, uploadedPhotos);
        if (uploadedUrls.length > 0) {
          await locauxService.update(result.id, { hasPhotos: true });
        }
      }
      // Save documents to documents table
      if (uploadedDocs.length > 0) {
        for (const docFile of uploadedDocs) {
          await documentsService.create(
            { type: 'Technique', local: result.title, localRef: result.ref },
            docFile
          );
        }
        await locauxService.update(result.id, { hasDocs: true });
      }
      if (publish) {
        toast.success('Local publié avec succès !');
      } else {
        toast.success('Local enregistré en brouillon');
      }
      setTimeout(() => router.push('/liste-des-locaux'), 1000);
    }
  };

  const inputCls = 'input-field';
  const labelCls = 'block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide';
  const sectionCls = 'bg-white rounded-xl border border-slate-200 p-6 shadow-sm';

  if (loadingEdit) {
    return (
      <div className="flex items-center justify-center py-32">
        <svg className="w-8 h-8 animate-spin text-primary-700" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  const handleAnalyzeText = async () => {
    const hasContent =
      form.commentairesPublics?.trim() ||
      form.commentairesConfidentiels?.trim() ||
      form.travauxNecessaires?.trim();

    if (!hasContent) {
      toast.error('Renseignez au moins un champ texte avant d\'analyser');
      return;
    }

    setAnalyzingText(true);
    setShowExtractedPanel(false);
    try {
      const result = await analyzeLocalText({
        commentairesPublics: form.commentairesPublics,
        commentairesConfidentiels: form.commentairesConfidentiels,
        travauxNecessaires: form.travauxNecessaires,
      });

      if (!result) {
        toast.error('Aucune donnée extractible trouvée dans les textes');
        setAnalyzingText(false);
        return;
      }

      setExtractedData(result);
      setShowExtractedPanel(true);
      toast.success('Analyse IA terminée — vérifiez les données extraites');
    } catch {
      toast.error('Erreur lors de l\'analyse IA');
    }
    setAnalyzingText(false);
  };

  const handleApplyExtracted = () => {
    if (!extractedData) return;

    setForm(prev => {
      const updated = { ...prev };
      if (extractedData.surfaceTotal && !prev.surfaceTotal) updated.surfaceTotal = String(extractedData.surfaceTotal);
      if (extractedData.surfaceRDC && !prev.surfaceRDC) updated.surfaceRDC = String(extractedData.surfaceRDC);
      if (extractedData.surfaceMezzanine && !prev.surfaceMezzanine) updated.surfaceMezzanine = String(extractedData.surfaceMezzanine);
      if (extractedData.surfaceSousSol && !prev.surfaceSousSol) updated.surfaceSousSol = String(extractedData.surfaceSousSol);
      if (extractedData.surfaceEtage && !prev.surfaceEtage) updated.surfaceEtage = String(extractedData.surfaceEtage);
      if (extractedData.surfaceTerrasse && !prev.surfaceTerrasse) updated.surfaceTerrasse = String(extractedData.surfaceTerrasse);
      if (extractedData.facade && !prev.facade) updated.facade = String(extractedData.facade);
      if (extractedData.lineaireVitrine && !prev.lineaireVitrine) updated.lineaireVitrine = String(extractedData.lineaireVitrine);
      if (extractedData.nbVitrines && !prev.nbVitrines) updated.nbVitrines = String(extractedData.nbVitrines);
      if (extractedData.loyer && !prev.loyer) updated.loyer = String(extractedData.loyer);
      if (extractedData.droitEntree && !prev.droitEntree) updated.droitEntree = String(extractedData.droitEntree);
      if (extractedData.pasDePorte && !prev.pasDePorte) updated.pasDePorte = String(extractedData.pasDePorte);
      if (extractedData.depotGarantie && !prev.depotGarantie) updated.depotGarantie = String(extractedData.depotGarantie);
      if (extractedData.disponibilite && !prev.disponibilite) updated.disponibilite = extractedData.disponibilite;
      if (extractedData.hauteurPlafond && !prev.hauteurPlafond) updated.hauteurPlafond = String(extractedData.hauteurPlafond);
      if (extractedData.puissanceElectrique && !prev.puissanceElectrique) updated.puissanceElectrique = extractedData.puissanceElectrique;
      if (extractedData.extraction !== undefined && !prev.extraction) updated.extraction = extractedData.extraction;
      if (extractedData.climatisation !== undefined && !prev.climatisation) updated.climatisation = extractedData.climatisation;
      if (extractedData.accessibilite !== undefined && !prev.accessibilite) updated.accessibilite = extractedData.accessibilite;
      if (extractedData.parking && !prev.parking) updated.parking = String(extractedData.parking);
      if (extractedData.livraison !== undefined && !prev.livraison) updated.livraison = extractedData.livraison;
      if (extractedData.etatLocal && !prev.etatLocal) updated.etatLocal = extractedData.etatLocal;
      if (extractedData.ville && !prev.ville) updated.ville = extractedData.ville;
      if (extractedData.quartier && !prev.quartier) updated.quartier = extractedData.quartier;
      if (extractedData.fluxPieton && !prev.fluxPieton) updated.fluxPieton = extractedData.fluxPieton;
      if (extractedData.visibiliteEnseigne && !prev.visibiliteEnseigne) updated.visibiliteEnseigne = extractedData.visibiliteEnseigne;
      if (extractedData.voisinage && !prev.voisinage) updated.voisinage = extractedData.voisinage;
      if (extractedData.proximiteTransports && !prev.proximiteTransports) updated.proximiteTransports = extractedData.proximiteTransports;
      // Always store AI extracted data for matching enrichment
      updated.aiExtractedData = extractedData;
      return updated;
    });

    toast.success('Données extraites appliquées au formulaire');
    setShowExtractedPanel(false);
  };

  return (
    <div className="px-6 lg:px-8 xl:px-10 py-7 max-w-screen-xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Link href="/liste-des-locaux" className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-700 text-slate-900">
              {mode === 'create' ? 'Nouveau local commercial' : 'Modifier le local'}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {mode === 'create' ? 'Remplissez les informations du local' : 'Mettez à jour les informations'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${completude >= 80 ? 'bg-emerald-500' : completude >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                style={{ width: `${completude}%` }}
              />
            </div>
            <span className="text-xs font-600 text-slate-600">{completude}% complété</span>
          </div>
          <button onClick={() => handleSave(false)} disabled={saving} className="btn-secondary">
            {saving ? 'Enregistrement...' : mode === 'edit' ? 'Sauvegarder' : 'Enregistrer brouillon'}
          </button>
          <button onClick={() => handleSave(true)} disabled={saving} className="btn-primary">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {mode === 'create' ? 'Publier le local' : 'Publier les modifications'}
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Section nav */}
        <div className="hidden lg:flex flex-col gap-1 w-52 flex-shrink-0">
          <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm sticky top-4">
            <p className="text-[10px] font-600 text-slate-400 uppercase tracking-widest px-2 mb-2">Sections</p>
            {SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => {
                  setActiveSection(s.id);
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150 text-left ${
                  activeSection === s.id
                    ? 'bg-primary-50 text-primary-700 font-600' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <span className="text-base">{s.icon}</span>
                <span className="truncate">{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Form sections */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* Identification */}
          <div id="identification" className={sectionCls}>
            <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2"><span>🏷️</span> Identification</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={labelCls}>Titre de la fiche</label>
                <input type="text" className={inputCls} placeholder="Ex: Avenue Anfa — Angle rue Molière, Casablanca" value={form.titre} onChange={e => set('titre', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Type de bien</label>
                <select className={inputCls} value={form.typeLocalCommercial} onChange={e => set('typeLocalCommercial', e.target.value)}>
                  {TYPES_LOCAL_COMMERCIAL.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Offre</label>
                <select className={inputCls} value={form.offre} onChange={e => set('offre', e.target.value)}>
                  {OFFRES.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Statut de publication</label>
                <select className={inputCls} value={form.statut} onChange={e => set('statut', e.target.value)}>
                  {STATUTS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Priorité commerciale</label>
                <select className={inputCls} value={form.priorite} onChange={e => set('priorite', e.target.value)}>
                  {['Basse', 'Normale', 'Haute', 'Urgente'].map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Source du lead</label>
                <select className={inputCls} value={form.source} onChange={e => set('source', e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {SOURCES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Broker en charge</label>
                <select className={inputCls} value={form.broker} onChange={e => set('broker', e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {brokers.length > 0
                    ? brokers.map(b => <option key={b.id} value={b.label}>{b.label}</option>)
                    : ['K. Alami', 'S. Benali', 'N. Tahiri', 'M. Ouali'].map(b => <option key={b}>{b}</option>)
                  }
                </select>
              </div>
            </div>
          </div>

          {/* Localisation */}
          <div id="localisation" className={sectionCls}>
            <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2"><span>📍</span> Localisation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Ville</label>
                <select className={inputCls} value={form.ville} onChange={e => set('ville', e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {VILLES.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Quartier</label>
                <input type="text" className={inputCls} placeholder="Ex: Maarif, Guéliz, Centre-Ville..." value={form.quartier} onChange={e => set('quartier', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Adresse complète</label>
                <input type="text" className={inputCls} placeholder="Numéro, rue, immeuble..." value={form.adresse} onChange={e => set('adresse', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Zone commerciale</label>
                <input type="text" className={inputCls} placeholder="Ex: Triangle d'Or..." value={form.zoneCommerciale} onChange={e => set('zoneCommerciale', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Type de local</label>
                <select className={inputCls} value={form.typeLocal} onChange={e => set('typeLocal', e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Latitude GPS</label>
                <input type="text" className={inputCls} placeholder="33.5731" value={form.lat} onChange={e => set('lat', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Longitude GPS</label>
                <input type="text" className={inputCls} placeholder="-7.6298" value={form.lng} onChange={e => set('lng', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Surfaces */}
          <div id="surfaces" className={sectionCls}>
            <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2"><span>📐</span> Surfaces (m²)</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div><label className={labelCls}>Surface totale</label><input type="number" className={inputCls} placeholder="0" value={form.surfaceTotal} onChange={e => set('surfaceTotal', e.target.value)} /></div>
              <div><label className={labelCls}>Surface RDC</label><input type="number" className={inputCls} placeholder="0" value={form.surfaceRDC} onChange={e => set('surfaceRDC', e.target.value)} /></div>
              <div><label className={labelCls}>Mezzanine</label><input type="number" className={inputCls} placeholder="0" value={form.surfaceMezzanine} onChange={e => set('surfaceMezzanine', e.target.value)} /></div>
              <div><label className={labelCls}>Sous-sol</label><input type="number" className={inputCls} placeholder="0" value={form.surfaceSousSol} onChange={e => set('surfaceSousSol', e.target.value)} /></div>
              <div><label className={labelCls}>Étage(s)</label><input type="number" className={inputCls} placeholder="0" value={form.surfaceEtage} onChange={e => set('surfaceEtage', e.target.value)} /></div>
              <div><label className={labelCls}>Terrasse</label><input type="number" className={inputCls} placeholder="0" value={form.surfaceTerrasse} onChange={e => set('surfaceTerrasse', e.target.value)} /></div>
            </div>
          </div>

          {/* Façade */}
          <div id="facade" className={sectionCls}>
            <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2"><span>🪟</span> Façade & Vitrine</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><label className={labelCls}>Façade linéaire (m)</label><input type="number" className={inputCls} placeholder="0" value={form.facade} onChange={e => set('facade', e.target.value)} /></div>
              <div><label className={labelCls}>Linéaire vitrine (m)</label><input type="number" className={inputCls} placeholder="0" value={form.lineaireVitrine} onChange={e => set('lineaireVitrine', e.target.value)} /></div>
              <div><label className={labelCls}>Nombre de vitrines</label><input type="number" className={inputCls} placeholder="0" value={form.nbVitrines} onChange={e => set('nbVitrines', e.target.value)} /></div>
            </div>
          </div>

          {/* Juridique */}
          <div id="juridique" className={sectionCls}>
            <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2"><span>⚖️</span> Informations juridiques</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Propriétaire</label><input type="text" className={inputCls} placeholder="Nom du propriétaire ou société" value={form.proprietaire} onChange={e => set('proprietaire', e.target.value)} /></div>
              <div>
                <label className={labelCls}>Type de détention</label>
                <select className={inputCls} value={form.typeDetention} onChange={e => set('typeDetention', e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {TYPES_DETENTION.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Titre foncier / Réf. acte</label><input type="text" className={inputCls} placeholder="Numéro de titre foncier" value={form.titreFoncier} onChange={e => set('titreFoncier', e.target.value)} /></div>
              <div>
                <label className={labelCls}>Usage autorisé</label>
                <select className={inputCls} value={form.usageAutorise} onChange={e => set('usageAutorise', e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {USAGES.map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div className="md:col-span-2"><label className={labelCls}>Note de renseignement</label><textarea className={inputCls} rows={3} placeholder="Informations juridiques complémentaires..." value={form.noteRenseignement} onChange={e => set('noteRenseignement', e.target.value)} /></div>
            </div>
          </div>

          {/* Commercial */}
          <div id="commercial" className={sectionCls}>
            <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2"><span>💰</span> Conditions commerciales</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Loyer mensuel (MAD)</label><input type="number" className={inputCls} placeholder="0" value={form.loyer} onChange={e => set('loyer', e.target.value)} /></div>
              <div><label className={labelCls}>Disponibilité</label><input type="text" className={inputCls} placeholder="Immédiate ou date (JJ/MM/AAAA)" value={form.disponibilite} onChange={e => set('disponibilite', e.target.value)} /></div>
              <div><label className={labelCls}>Droit d'entrée (MAD)</label><input type="number" className={inputCls} placeholder="0" value={form.droitEntree} onChange={e => set('droitEntree', e.target.value)} /></div>
              <div><label className={labelCls}>Pas-de-porte (MAD)</label><input type="number" className={inputCls} placeholder="0" value={form.pasDePorte} onChange={e => set('pasDePorte', e.target.value)} /></div>
              <div><label className={labelCls}>Dépôt de garantie (MAD)</label><input type="number" className={inputCls} placeholder="0" value={form.depotGarantie} onChange={e => set('depotGarantie', e.target.value)} /></div>
              <div className="flex items-center gap-3 pt-6">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={form.exclusivite} onChange={e => set('exclusivite', e.target.checked)} />
                  <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-700"></div>
                  <span className="ml-2 text-sm font-500 text-slate-700">Exclusivité commerciale</span>
                </label>
              </div>
            </div>
          </div>

          {/* Technique */}
          <div id="technique" className={sectionCls}>
            <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2"><span>🔧</span> Caractéristiques techniques</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Hauteur sous plafond (m)</label><input type="number" step="0.1" className={inputCls} placeholder="3.5" value={form.hauteurPlafond} onChange={e => set('hauteurPlafond', e.target.value)} /></div>
              <div><label className={labelCls}>Puissance électrique (kVA)</label><input type="number" className={inputCls} placeholder="0" value={form.puissanceElectrique} onChange={e => set('puissanceElectrique', e.target.value)} /></div>
              <div><label className={labelCls}>Places de parking</label><input type="number" className={inputCls} placeholder="0" value={form.parking} onChange={e => set('parking', e.target.value)} /></div>
              <div>
                <label className={labelCls}>État du local</label>
                <select className={inputCls} value={form.etatLocal} onChange={e => set('etatLocal', e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {ETATS.map(e => <option key={e}>{e}</option>)}
                </select>
              </div>
              <div className="md:col-span-2"><label className={labelCls}>Travaux nécessaires</label><textarea className={inputCls} rows={2} placeholder="Description des travaux à prévoir..." value={form.travauxNecessaires} onChange={e => set('travauxNecessaires', e.target.value)} /></div>
              <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { key: 'extraction', label: 'Extraction' },
                  { key: 'climatisation', label: 'Climatisation' },
                  { key: 'accessibilite', label: 'Accessibilité PMR' },
                  { key: 'livraison', label: 'Accès livraison' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                    <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-primary-700 focus:ring-primary-700" checked={form[key as keyof FormData] as boolean} onChange={e => set(key as keyof FormData, e.target.checked)} />
                    <span className="text-sm font-500 text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Environnement */}
          <div id="environnement" className={sectionCls}>
            <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2"><span>🌍</span> Environnement commercial</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Flux piéton estimé</label>
                <select className={inputCls} value={form.fluxPieton} onChange={e => set('fluxPieton', e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {['Très fort', 'Fort', 'Moyen', 'Faible', 'Très faible'].map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Visibilité de l'enseigne</label>
                <select className={inputCls} value={form.visibiliteEnseigne} onChange={e => set('visibiliteEnseigne', e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {['Excellente', 'Bonne', 'Moyenne', 'Limitée'].map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Voisinage commercial</label><input type="text" className={inputCls} placeholder="Ex: Zara, McDonald's, Pharmacie..." value={form.voisinage} onChange={e => set('voisinage', e.target.value)} /></div>
              <div><label className={labelCls}>Proximité transports</label><input type="text" className={inputCls} placeholder="Ex: Tram ligne 1 (200m), Bus..." value={form.proximiteTransports} onChange={e => set('proximiteTransports', e.target.value)} /></div>
            </div>
          </div>

          {/* Notes */}
          <div id="notes" className={sectionCls}>
            <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2"><span>📝</span> Notes & Commentaires</h2>
            <div className="space-y-4">
              <div><label className={labelCls}>Commentaires publics</label><textarea className={inputCls} rows={4} placeholder="Description visible par les locataires potentiels..." value={form.commentairesPublics} onChange={e => set('commentairesPublics', e.target.value)} /></div>
              <div>
                <label className={labelCls}>
                  <span className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.347.347a3.5 3.5 0 01-4.95 0l-.347-.347z" />
                    </svg>
                    Commentaires confidentiels (équipe interne uniquement)
                  </span>
                </label>
                <textarea className={inputCls} rows={3} placeholder="Notes internes, contexte confidentiel..." value={form.commentairesConfidentiels} onChange={e => set('commentairesConfidentiels', e.target.value)} />
              </div>

              {/* AI Analysis Button */}
              <div className="pt-2 border-t border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs font-600 text-slate-700 uppercase tracking-wide">Analyse IA des textes</p>
                    <p className="text-xs text-slate-400 mt-0.5">L'IA extrait automatiquement les données clés des champs texte ci-dessus et des travaux nécessaires</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAnalyzeText}
                    disabled={analyzingText}
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white text-sm font-600 rounded-lg transition-colors"
                  >
                    {analyzingText ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Analyse en cours...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.347.347a3.5 3.5 0 01-4.95 0l-.347-.347z" />
                        </svg>
                        Analyser avec l'IA
                      </>
                    )}
                  </button>
                </div>

                {/* Extracted Data Panel */}
                {showExtractedPanel && extractedData && (
                  <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm font-700 text-violet-900">Données extraites par l'IA</p>
                        {extractedData.extractionSummary && (
                          <p className="text-xs text-violet-600 mt-0.5">{extractedData.extractionSummary}</p>
                        )}
                      </div>
                      <button onClick={() => setShowExtractedPanel(false)} className="text-violet-400 hover:text-violet-600 p-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                      {extractedData.surfaceTotal && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Surface totale</p><p className="text-sm font-600 text-slate-800">{extractedData.surfaceTotal} m²</p></div>}
                      {extractedData.surfaceRDC && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Surface RDC</p><p className="text-sm font-600 text-slate-800">{extractedData.surfaceRDC} m²</p></div>}
                      {extractedData.surfaceMezzanine && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Mezzanine</p><p className="text-sm font-600 text-slate-800">{extractedData.surfaceMezzanine} m²</p></div>}
                      {extractedData.surfaceSousSol && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Sous-sol</p><p className="text-sm font-600 text-slate-800">{extractedData.surfaceSousSol} m²</p></div>}
                      {extractedData.surfaceEtage && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Étage</p><p className="text-sm font-600 text-slate-800">{extractedData.surfaceEtage} m²</p></div>}
                      {extractedData.surfaceTerrasse && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Terrasse</p><p className="text-sm font-600 text-slate-800">{extractedData.surfaceTerrasse} m²</p></div>}
                      {extractedData.facade && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Façade</p><p className="text-sm font-600 text-slate-800">{extractedData.facade} m</p></div>}
                      {extractedData.loyer && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Loyer</p><p className="text-sm font-600 text-slate-800">{extractedData.loyer.toLocaleString('fr-FR')} MAD</p></div>}
                      {extractedData.hauteurPlafond && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Hauteur plafond</p><p className="text-sm font-600 text-slate-800">{extractedData.hauteurPlafond} m</p></div>}
                      {extractedData.disponibilite && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Disponibilité</p><p className="text-sm font-600 text-slate-800">{extractedData.disponibilite}</p></div>}
                      {extractedData.etatLocal && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100"><p className="text-[10px] text-slate-400 uppercase tracking-wide">État</p><p className="text-sm font-600 text-slate-800">{extractedData.etatLocal}</p></div>}
                      {extractedData.voisinage && <div className="bg-white rounded-lg px-3 py-2 border border-violet-100 col-span-2"><p className="text-[10px] text-slate-400 uppercase tracking-wide">Voisinage</p><p className="text-sm font-600 text-slate-800">{extractedData.voisinage}</p></div>}
                    </div>

                    {extractedData.matchingKeywords && extractedData.matchingKeywords.length > 0 && (
                      <div className="mb-3">
                        <p className="text-[10px] text-violet-500 uppercase tracking-wide font-600 mb-1.5">Mots-clés matching</p>
                        <div className="flex flex-wrap gap-1.5">
                          {extractedData.matchingKeywords.map((kw, i) => (
                            <span key={i} className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full font-500">{kw}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={handleApplyExtracted}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-600 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        Appliquer les données extraites
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowExtractedPanel(false)}
                        className="px-4 py-2 bg-white border border-violet-200 text-violet-700 text-sm font-600 rounded-lg hover:bg-violet-50 transition-colors"
                      >
                        Ignorer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Media upload */}
          <div className={sectionCls}>
            <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2"><span>📸</span> Médias & Documents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input ref={photoInputRef} type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" multiple onChange={e => { const files = Array.from(e.target.files || []); setUploadedPhotos(prev => [...prev, ...files]); if (files.length > 0) toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} ajoutée${files.length > 1 ? 's' : ''}`); }} />
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer" onClick={() => photoInputRef.current?.click()}>
                  <svg className="w-10 h-10 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v3M4 7h16" />
                </svg>
                  <p className="text-sm font-600 text-slate-600">Photos du local</p>
                  <p className="text-xs text-slate-400 mt-1">Cliquez pour sélectionner des photos</p>
                </div>

                {/* Existing photos (edit mode) */}
                {existingPhotos.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-600 text-slate-500 uppercase tracking-wide mb-2">Photos existantes ({existingPhotos.length})</p>
                    <div className="grid grid-cols-3 gap-2">
                      {existingPhotos.map((url, i) => (
                        <div key={i} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square">
                          <img src={url} alt={`Photo existante ${i + 1}`} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                            <button
                              onClick={() => handleDeleteExistingPhoto(url)}
                              disabled={deletingPhoto === url}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                              title="Supprimer cette photo"
                            >
                              {deletingPhoto === url ? (
                                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                              ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New photos to upload */}
                {uploadedPhotos.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    <p className="text-xs font-600 text-slate-500 uppercase tracking-wide mb-1">Nouvelles photos à uploader</p>
                    {uploadedPhotos.map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                        <span className="text-slate-700 truncate flex-1">{f.name}</span>
                        <button onClick={() => setUploadedPhotos(prev => prev.filter((_, idx) => idx !== i))} className="ml-2 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <input ref={docInputRef} type="file" className="hidden" accept=".pdf,.dwg,.doc,.docx,.xls,.xlsx" multiple onChange={e => { const files = Array.from(e.target.files || []); setUploadedDocs(prev => [...prev, ...files]); if (files.length > 0) toast.success(`${files.length} document${files.length > 1 ? 's' : ''} ajouté${files.length > 1 ? 's' : ''}`); }} />
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer" onClick={() => docInputRef.current?.click()}>
                  <svg className="w-10 h-10 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  <p className="text-sm font-600 text-slate-600">Documents</p>
                  <p className="text-xs text-slate-400 mt-1">Plans, titre foncier, bail, diagnostics...</p>
                </div>
                {uploadedDocs.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {uploadedDocs.map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                        <span className="text-slate-700 truncate flex-1">{f.name}</span>
                        <button onClick={() => setUploadedDocs(prev => prev.filter((_, idx) => idx !== i))} className="ml-2 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom actions */}
          <div className="flex items-center justify-between pt-2 pb-8">
            <Link href="/liste-des-locaux" className="btn-secondary">Annuler</Link>
            <div className="flex items-center gap-3">
              <button onClick={() => handleSave(false)} disabled={saving} className="btn-secondary">
                {mode === 'edit' ? 'Sauvegarder' : 'Enregistrer brouillon'}
              </button>
              <button onClick={() => handleSave(true)} disabled={saving} className="btn-primary">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                {mode === 'create' ? 'Publier le local' : 'Publier les modifications'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
